import React, { useState, useEffect } from 'react';
import { Toast } from '../../../../components/common/Toast';
import SectionCard from '../../../../components/common/SectionCard';
import ToggleField from '../../../../components/common/ToggleField';
import InputField from '../../../../components/common/InputField';
import { ColorPaletteIcon, CookieComplianceIcon, CustomizationIcon } from '../../../../components/common/SvgIcon';
import PositionPreview from '../PositionPreview';
import ColorField from '../ColorField';
import Skeleton from '../../../../components/common/Skeleton';

const save = `${LEGAL_PAGES.assets_url}/images/icons/save.png`;

const POSITIONS = [
  { value: 'bottom',       label: 'Bottom Bar',    bars: [{ pos: 'bottom', full: true }] },
  { value: 'bottom-left',  label: 'Bottom Left',   bars: [{ pos: 'bottom', full: false, align: 'left' }] },
  { value: 'bottom-right', label: 'Bottom Right',  bars: [{ pos: 'bottom', full: false, align: 'right' }] },
  { value: 'top',          label: 'Top Bar',       bars: [{ pos: 'top', full: true }] },
  { value: 'top-left',     label: 'Top Left',      bars: [{ pos: 'top', full: false, align: 'left' }] },
  { value: 'top-right',    label: 'Top Right',     bars: [{ pos: 'top', full: false, align: 'right' }] },
];

const GCookieSettings = ({ isLoading, setIsLoading }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    show_cookie_warning: false,
    auto_reload: false,
    cookie_display_position: 'bottom',
    cookie_policy_link: '',
    learn_more_link_text: '',
    cookie_message: '',
    cookie_dismiss_button: '',
    cookie_deny_button: '',
    cookie_accept_button: '',
    cookie_message_bg: '#1f2937',
    cookie_message_color: '#ffffff',
    cookie_button_bg: '#7c3aed',
    cookie_button_text_color: '#ffffff',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-settings`, {
        headers: { 'X-WP-Nonce': LEGAL_PAGES.nonce },
      });
      const result = await response.json();
      if (result.success && result.data) {
        setFormData(prev => ({ ...prev, ...result.data }));
      }
    } catch (e) {
      Toast.error('Failed to load cookie settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        Toast.success('Cookie settings saved successfully!');
        if (result.data) setFormData(prev => ({ ...prev, ...result.data }));
      } else {
        Toast.error('Failed to save cookie settings.');
      }
    } catch (e) {
      Toast.error('An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="space-y-6">

        {/* EU Cookie Compliance */}
        <SectionCard
          title="EU Cookie Compliance"
          icon={
            <CookieComplianceIcon />
          }
        >
          {isLoading ? <Skeleton /> : (
            <div className="space-y-6">
              <div className='bg-[#F4F4F580] p-2 rounded-xl'>
                <ToggleField
                  label="Show EU Cookie Alert"
                  description="Display cookie consent notification to users"
                  name="show_cookie_warning"
                  checked={formData.show_cookie_warning}
                  onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                />
              </div>
              <div className='bg-[#F4F4F580] p-2 rounded-xl'>
                <ToggleField
                  label="Auto-reload After Consent"
                  description="Reload the page after user accepts cookie"
                  name="auto_reload"
                  checked={formData.auto_reload}
                  onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                />
              </div>

              {/* Position picker */}
              <div>
                <label className="block text-sm font-medium text-[#17171C] mb-3">
                  Cookie Bar Position
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {POSITIONS.map(pos => (
                    <PositionPreview
                      key={pos.value}
                      config={pos}
                      selected={formData.cookie_display_position}
                      onSelect={(val) => setFormData(prev => ({ ...prev, cookie_display_position: val }))}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* Customization */}
        <SectionCard
          title="Customization"
          icon={<CustomizationIcon />}
        >
          {isLoading ? <Skeleton /> : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Cookie Policy Link"
                  name="cookie_policy_link"
                  value={formData.cookie_policy_link}
                  onChange={handleInputChange}
                  placeholder="https://www.cookiesandyou.com/"
                />
                <InputField
                  label="Learn More Link Text"
                  name="learn_more_link_text"
                  value={formData.learn_more_link_text}
                  onChange={handleInputChange}
                  placeholder="Learn more"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#17171C] mb-1.5">Cookie Message</label>
                <textarea
                  name="cookie_message"
                  value={formData.cookie_message}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="This website uses cookies to ensure you get the best experience on our website."
                  className="h-[80px] w-full !rounded-xl border !border-[#E4E4E7] px-4 text-sm text-[#17171C] focus:!border-[#17171C] focus:!shadow-none focus:!ring-0 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* <InputField
                  label="Dismiss Button Text"
                  name="cookie_dismiss_button"
                  value={formData.cookie_dismiss_button}
                  onChange={handleInputChange}
                  placeholder="Dismiss"
                /> */}
                <InputField
                  label="Deny Button Text"
                  name="cookie_deny_button"
                  value={formData.cookie_deny_button}
                  onChange={handleInputChange}
                  placeholder="Deny"
                />
                <InputField
                  label="Accept Button Text"
                  name="cookie_accept_button"
                  value={formData.cookie_accept_button}
                  onChange={handleInputChange}
                  placeholder="Accept"
                />
              </div>
            </div>
          )}
        </SectionCard>

        {/* Colors */}
        <SectionCard
          title="Colors"
          icon={<ColorPaletteIcon />}
        >
          {isLoading ? <Skeleton /> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ColorField
                label="Banner Background"
                name="cookie_message_bg"
                value={formData.cookie_message_bg}
                onChange={handleInputChange}
              />
              <ColorField
                label="Banner Text Color"
                name="cookie_message_color"
                value={formData.cookie_message_color}
                onChange={handleInputChange}
              />
              <ColorField
                label="Button Background"
                name="cookie_button_bg"
                value={formData.cookie_button_bg}
                onChange={handleInputChange}
              />
              <ColorField
                label="Button Text Color"
                name="cookie_button_text_color"
                value={formData.cookie_button_text_color}
                onChange={handleInputChange}
              />
            </div>
          )}
        </SectionCard>

        {/* Save */}
        <div className="flex items-center justify-start gap-2 rounded-xl p-4">
          <button
            type="submit"
            disabled={saving}
            className={[
              'inline-flex items-center cursor-pointer justify-center rounded-xl px-6 py-[14px] text-sm font-semibold text-white shadow-[0px_4px_14px_-2px_#7C3BED40] transition',
              saving ? 'cursor-not-allowed bg-indigo-400' : 'bg-[#7C3BED] hover:bg-indigo-700',
            ].join(' ')}
          >
            <img src={save} alt="Save" className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default GCookieSettings;